import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import KpiCard from "@/components/KpiCard";
import TrustBadge from "@/components/TrustBadge";
import TrustRadar from "@/components/TrustRadar";
import HcahpsPanel from "@/components/HcahpsPanel";
import HcahpsStars from "@/components/HcahpsStars";
import HospitalCommandTabs from "@/components/hospital/CommandTabs";
import CroInsightsInfographic from "@/components/hospital/CroInsightsInfographic";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { AlertTriangle, Timer, Bed, HeartPulse, ArrowRight, Star } from "lucide-react";
import api from "@/lib/api";

export default function HospitalDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/hospital/dashboard");
      setData(data);
    })();
  }, []);

  if (!data) return <AppShell pageTitle="Hospital dashboard"><div className="text-sm text-muted-foreground">Loading…</div></AppShell>;

  const criteria = Object.entries(data.trust_metrics || {}).map(([name, score]) => ({ name, score }));

  return (
    <AppShell
      pageTitle={data.hospital.name}
      pageDescription={`${data.hospital.type} · ${data.hospital.city} · ${data.hospital.accreditation}`}
      actions={<TrustBadge tier={data.tier} score={data.trust_score} />}
    >
      <HospitalCommandTabs />
      <CroInsightsInfographic />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Trust Index" value={data.trust_score} icon={HeartPulse} tone="sky" testid="hospital-kpi-trust" />
        <KpiCard label="HCAHPS · summary" value={data.hcahps?.summary_score ?? "—"} unit={`(${data.hcahps?.summary_stars ?? 0}★)`} icon={Star} tone="lavender" testid="hospital-kpi-hcahps" />
        <KpiCard label="Avg. waiting time" value={data.kpis.avg_waiting_time_minutes} unit="min" icon={Timer} tone="lemon" testid="hospital-kpi-wait" />
        <KpiCard label="Bed occupancy" value={data.kpis.bed_occupancy_pct} unit="%" icon={Bed} tone="mint" testid="hospital-kpi-beds" />
      </div>

      <div className="mt-6">
        <HcahpsPanel data={buildHcahpsData(data.hospital.id, data.hospital.name, data.hcahps)} />
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Weekly waiting time</h2>
            <span className="text-xs text-muted-foreground">Median across departments</span>
          </div>
          <div style={{ width: "100%", height: 260 }} data-testid="waiting-time-chart">
            <ResponsiveContainer>
              <LineChart data={data.waiting_time_series}>
                <CartesianGrid stroke="#E5E3DB" strokeDasharray="3 3" />
                <XAxis dataKey="day" tick={{ fill: "#5C6B61", fontSize: 11 }} />
                <YAxis tick={{ fill: "#5C6B61", fontSize: 11 }} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E3DB", borderRadius: 8 }} />
                <Line type="monotone" dataKey="minutes" stroke="#C06E52" strokeWidth={2.5} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <h2 className="font-serif text-2xl text-primary mb-3">Trust breakdown</h2>
          <TrustRadar criteria={criteria} height={260} />
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Department benchmarks</h2>
          </div>
          <ul className="divide-y divide-border" data-testid="departments-list">
            {data.departments.map((d) => (
              <li key={d.name} className="py-3 flex items-center justify-between text-sm">
                <div className="flex-1">
                  <div className="font-medium text-primary">{d.name}</div>
                  <div className="text-xs text-muted-foreground">
                    Wait {d.waiting_time_min}m · Experience {d.patient_experience}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-primary font-semibold">{d.trust_score}</div>
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Trust</div>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-lg border border-border bg-white p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="font-serif text-2xl text-primary">Recent incidents</h2>
            <Link to="/hospital/incidents" className="text-sm text-primary hover:underline inline-flex items-center gap-1" data-testid="link-hospital-incidents">
              Manage <ArrowRight size={14} />
            </Link>
          </div>
          {data.incidents.length === 0 ? (
            <div className="text-sm text-muted-foreground">No incidents recorded.</div>
          ) : (
            <ul className="space-y-3" data-testid="recent-incidents-list">
              {data.incidents.map((i) => (
                <li key={i.id} className="text-sm">
                  <div className="flex items-center gap-2">
                    <SeverityDot severity={i.severity} />
                    <span className="font-medium text-primary capitalize">{i.category}</span>
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{i.status}</span>
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5 ml-4">{i.description}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function SeverityDot({ severity }) {
  const color =
    severity === "critical" ? "#C0392B" :
    severity === "high" ? "#C06E52" :
    severity === "medium" ? "#EAB308" :
    "#7A8B76";
  return <span className="w-2 h-2 rounded-full" style={{ background: color }} />;
}

/** Transform embedded HCAHPS object into the shape HcahpsPanel expects. */
function buildHcahpsData(hospitalId, hospitalName, hcahps) {
  if (!hcahps) return null;
  const order = [
    "communication_nurses", "communication_doctors", "responsiveness",
    "medicines", "discharge", "care_transition",
    "cleanliness", "quietness",
    "overall_rating", "recommend",
  ];
  const measures = order
    .filter((k) => hcahps.measures && hcahps.measures[k])
    .map((k) => ({ key: k, ...hcahps.measures[k] }));
  return {
    hospital_id: hospitalId,
    hospital_name: hospitalName,
    measures,
    summary_score: hcahps.summary_score,
    summary_stars: hcahps.summary_stars,
    source: hcahps.source,
    policy_version: hcahps.policy_version,
  };
}
